import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, MapPin, Clock, GraduationCap, Home, TrendingUp, Users, CheckCircle, HelpCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { trackEvent, getNeighbourhoods } from '../lib/api';
import SEO from '../components/SEO';

export default function BurlingtonPage() {
  const [neighbourhoods, setNeighbourhoods] = useState([]);

  useEffect(() => {
    trackEvent({ event_type: 'page_view', page: 'burlington-on' });
    getNeighbourhoods().then(r => setNeighbourhoods(r.data.neighbourhoods || [])).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-16" data-testid="burlington-on-page">
      <SEO
        title="Burlington, Ontario — The Complete Guide to Living, Working & Buying"
        description="Burlington Ontario: population, neighbourhoods, schools, commute, real estate, waterfront lifestyle, parks, demographics, cost of living, and why families are moving here. The most comprehensive Burlington guide online."
        canonical="/burlington-on"
        type="article"
        article={{ datePublished: '2025-01-15', dateModified: '2026-02-27' }}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Burlington, Ontario', url: '/burlington-on' },
        ]}
        faq={[
          { q: 'Is Burlington expensive?', a: 'More affordable than Toronto and Oakville, but higher than many Ontario cities. Condos start from $425K, detached from $800K. You typically get significantly more space per dollar than Toronto.' },
          { q: 'Is Burlington safe?', a: "Yes. Burlington and the broader Halton Region consistently rank among Ontario's safest municipalities. Crime rates are well below the GTA average." },
          { q: 'How far is Burlington from Toronto?', a: 'Approximately 50 km. 45-60 minutes by car depending on traffic, or 35-50 minutes by GO Train from Burlington GO to Union Station.' },
          { q: 'What are the best neighbourhoods in Burlington?', a: "It depends on your priorities. Roseland and Shoreacres for luxury, Alton Village and Orchard for families, Burlington East for value, Downtown for walkability." },
          { q: 'Is Burlington good for families?', a: "Very much so. The Halton District School Board is one of Ontario's best, there are 300+ parks, extensive recreation programs, low crime, and a family-oriented community culture." },
          { q: 'Is the commute from Burlington to Toronto manageable?', a: "For hybrid workers (2-3 days per week), yes. GO Train runs every 15-30 minutes during peak and takes 35-50 minutes." },
          { q: 'What is the population of Burlington?', a: 'Approximately 190,000 as of the latest census. Growth is controlled due to greenbelt protections.' },
          { q: 'Does Burlington have good restaurants?', a: "Yes — especially downtown. Brant Street alone has 50+ restaurants, from upscale Italian to craft cocktail bars." },
          { q: 'What is Burlington known for?', a: "Waterfront living, the Royal Botanical Gardens, Spencer Smith Park, top-ranked schools, the Sound of Music Festival, and being one of Canada's most livable cities." },
          { q: 'Is Burlington better than Oakville?', a: "Burlington is more affordable, has a stronger waterfront identity, and shares the same school board (Halton). Oakville has more established luxury areas and slightly shorter GO commute." },
        ]}
      />

      {/* ─── Hero ─── */}
      <section className="relative py-24 md:py-36 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/yr1apojk_shutterstock_2665884737.jpg" alt="Burlington waterfront pier at sunset" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/60 to-slate-900/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/15 rounded-full px-4 py-1.5 mb-6">
              <MapPin className="w-3 h-3 text-emerald-400" />
              <span className="text-white/70 text-xs font-medium tracking-wide">Halton Region, Ontario, Canada</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-normal leading-[1.08] tracking-tight text-white mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Burlington, <span className="italic">Ontario</span>
            </h1>
            <p className="text-xl text-white/60 leading-relaxed mb-4">
              The Complete Guide to Living, Working & Buying in Burlington
            </p>
            <p className="text-base text-white/40 leading-relaxed max-w-xl">
              Population ~190,000. Lake Ontario waterfront. Halton Region. One of Canada's most desirable cities — and this is the most comprehensive guide to it.
            </p>
          </div>
        </div>
      </section>

      {/* ─── Table of Contents ─── */}
      <section className="border-b border-slate-100 py-8 bg-white sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-6 overflow-x-auto text-[12px] font-medium text-slate-400 whitespace-nowrap pb-1 scrollbar-none">
            {[
              { id: 'location', label: 'Location' },
              { id: 'history', label: 'History' },
              { id: 'neighbourhoods', label: 'Neighbourhoods' },
              { id: 'schools', label: 'Schools' },
              { id: 'commute', label: 'Commute' },
              { id: 'parks', label: 'Parks & Recreation' },
              { id: 'demographics', label: 'Demographics' },
              { id: 'cost', label: 'Cost of Living' },
              { id: 'realestate', label: 'Real Estate' },
              { id: 'why', label: 'Why Burlington' },
              { id: 'faq', label: 'FAQ' },
            ].map(item => (
              <a key={item.id} href={`#${item.id}`} className="hover:text-slate-900 transition-colors">{item.label}</a>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 py-16">
          {/* Main Content */}
          <article className="lg:col-span-8 space-y-16">

            {/* Intro */}
            <div>
              <p className="text-lg text-slate-700 leading-relaxed mb-4">
                Burlington, Ontario is one of Canada's most desirable cities — and for good reason.
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                Perfectly positioned between Toronto and Hamilton along the northwestern shore of Lake Ontario, Burlington offers the rare combination of waterfront lifestyle, top-tier schools, safe neighbourhoods, and commuter convenience.
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                With a population of roughly 190,000 residents and part of the Halton Region, Burlington consistently ranks among the best places to live in Canada.
              </p>
              <p className="text-base text-slate-500 leading-relaxed">
                Whether you're relocating from Toronto, upgrading for more space, downsizing near the lake, or simply exploring your options — this is your complete guide to Burlington.
              </p>
            </div>

            {/* Location */}
            <section id="location" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Where is Burlington, <span className="italic">Ontario?</span>
              </h2>
              <p className="text-base text-slate-500 leading-relaxed mb-6">
                Burlington is located in Southern Ontario along Lake Ontario's western shoreline. Its location is one of its greatest assets — you get lake access, green space, and suburban comfort without losing connectivity to the Greater Toronto Area.
              </p>
              <div className="bento-grid grid-cols-2 md:grid-cols-3 rounded-2xl mb-6">
                {[
                  { label: 'To Toronto', value: '45-60 min' },
                  { label: 'To Hamilton', value: '20 min' },
                  { label: 'Region', value: 'Halton' },
                  { label: 'Highways', value: 'QEW, 403, 407' },
                  { label: 'GO Stations', value: '2 (Burlington, Appleby)' },
                  { label: 'Lake', value: 'Ontario (northwest shore)' },
                ].map(item => (
                  <div key={item.label} className="p-4">
                    <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-1">{item.label}</p>
                    <p className="text-sm font-medium text-slate-900">{item.value}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* History */}
            <section id="history" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
                A brief history of <span className="italic">Burlington</span>
              </h2>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                Before European settlement, the land that is now Burlington was inhabited by Indigenous peoples, including the Mississaugas of the Credit First Nation. The area's position along Lake Ontario and at the head of Burlington Bay made it strategically significant for centuries before colonization.
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                In the 18th and 19th centuries, Burlington developed as an agricultural and shipping community along Lake Ontario. The town's fertile soil supported orchards and farms — a heritage still reflected in neighbourhood names like "Orchard" and the agricultural character of areas like Kilbride. The opening of the Great Western Railway in 1854 connected Burlington to Hamilton and Toronto, accelerating growth.
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                By the mid-20th century, suburban expansion from Toronto transformed Burlington from a small town into a commuter city. The post-war building boom created many of the established neighbourhoods — Tyandaga, Roseland, LaSalle — that remain among Burlington's most desirable areas today. The Queen Elizabeth Way (QEW) highway, completed in 1940, cemented Burlington's connection to Toronto.
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                Burlington was incorporated as a city in 1974, merging with surrounding townships. Since then, growth has been deliberate: the city expanded northward through Alton Village (2000s) and Freeman (2010s-present), while protecting greenspace through Ontario's Greenbelt legislation.
              </p>
              <p className="text-base text-slate-500 leading-relaxed">
                Waterfront redevelopment — especially around Spencer Smith Park, the Brant Street Pier, and the downtown revitalization — has transformed Burlington's identity from bedroom community to a city with genuine cultural and lifestyle appeal. Today, Burlington balances modern suburban development with preserved greenbelt lands and heritage neighbourhoods.
              </p>
            </section>

            {/* CTA 1 */}
            <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Considering Burlington?</h3>
                <p className="text-sm text-slate-500">Find which 2-3 neighbourhoods match your budget, timeline, and family needs.</p>
              </div>
              <Link to="/blueprint" className="flex-shrink-0">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                  Start My Blueprint <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Neighbourhoods */}
            <section id="neighbourhoods" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <h2 className="text-3xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Burlington neighbourhoods <span className="italic">explained</span>
              </h2>
              <p className="text-base text-slate-500 leading-relaxed mb-8">
                One reason Burlington ranks so highly is its diverse neighbourhood options. Burlington isn't one uniform suburb — it's 21 distinct areas across 8 RAHB zones, each with different pricing, character, school catchments, and commute profiles. Choosing the right neighbourhood is often more important than choosing the right house.
              </p>
              <div className="space-y-4">
                {neighbourhoods.map(n => (
                  <Link key={n.slug} to={`/neighbourhoods/${n.slug}`} className="block bg-white rounded-2xl border border-slate-200/80 p-5 card-interactive">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-medium text-slate-900" style={{ fontFamily: "'Instrument Serif', serif" }}>{n.name}</h3>
                      <ArrowRight className="w-4 h-4 text-slate-300 mt-1" />
                    </div>
                    <p className="text-[13px] text-slate-500 leading-relaxed mb-3">{n.vibe_summary}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {n.tags?.filter(t => !t.startsWith('has_')).slice(0, 4).map(t => <span key={t} className="text-[10px] font-mono bg-slate-50 text-slate-500 px-2 py-0.5 rounded-full">{t.replace(/_/g, ' ')}</span>)}
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA 2 */}
            <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Not sure which area fits?</h3>
                <p className="text-sm text-slate-500">The Buyer Blueprint scores all 21 neighbourhoods against your specific priorities.</p>
              </div>
              <Link to="/blueprint" className="flex-shrink-0">
                <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                  Find My Neighbourhood <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            {/* Schools */}
            <section id="schools" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <div className="flex items-center gap-2 mb-6">
                <GraduationCap className="w-5 h-5 text-emerald-600" />
                <h2 className="text-3xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Schools in <span className="italic">Burlington</span></h2>
              </div>
              <p className="text-base text-slate-500 leading-relaxed mb-6">
                Families consistently cite schools as a major relocation factor — and Burlington delivers. The city sits entirely within the Halton District School Board, one of Ontario's highest-performing boards, with EQAO scores consistently above provincial averages.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-emerald-50/50 rounded-2xl p-5 border border-emerald-100/50">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-emerald-600 mb-3">Public Schools (HDSB)</p>
                  <div className="space-y-2 text-[13px] text-slate-600">
                    <p>Halton District School Board operates all public schools in Burlington. Consistently ranked top-3 in Ontario. Strong special education support. French immersion available at select schools (high demand — register early).</p>
                    <p>Notable schools: Alton Village PS, Orchard Park PS, Paul Fisher PS, LaSalle Park PS, Central PS.</p>
                  </div>
                </div>
                <div className="bg-blue-50/50 rounded-2xl p-5 border border-blue-100/50">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-blue-600 mb-3">Catholic Schools (HCDSB)</p>
                  <div className="space-y-2 text-[13px] text-slate-600">
                    <p>Halton Catholic District School Board provides faith-based education with strong academics and community involvement. Publicly funded in Ontario. Available to Catholic families or those directing education taxes.</p>
                    <p>Notable schools: St. Anne, Corpus Christi CSS, Assumption CSS.</p>
                  </div>
                </div>
              </div>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                French immersion options are widely available through HDSB, though demand consistently exceeds supply. Private school options exist both in Burlington and nearby Oakville. Burlington schools are often ranked among Ontario's strongest, contributing significantly to property value stability.
              </p>
              <p className="text-[11px] text-slate-400 italic">School catchment boundaries change. Always verify with the relevant board before making a purchase decision based on school access.</p>
              <div className="mt-4">
                <Link to="/schools" className="text-sm text-emerald-600 hover:text-emerald-700 transition-colors flex items-center gap-1">
                  Read the full Burlington Schools Guide <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </section>

            {/* Commute */}
            <section id="commute" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <div className="flex items-center gap-2 mb-6">
                <Clock className="w-5 h-5 text-slate-500" />
                <h2 className="text-3xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Commuting from <span className="italic">Burlington</span></h2>
              </div>
              <p className="text-base text-slate-500 leading-relaxed mb-6">
                Burlington has two GO stations — Burlington GO (downtown) and Appleby GO (north-central) — both on the Lakeshore West line, GO Transit's most frequent service corridor. Commute time to Toronto Union Station averages 35-55 minutes depending on schedule and station.
              </p>
              <div className="bento-grid grid-cols-1 md:grid-cols-2 rounded-2xl mb-6">
                <div className="p-5">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">GO Transit</p>
                  <p className="text-sm font-medium text-slate-900 mb-1">Burlington GO → Union Station</p>
                  <p className="font-mono text-2xl font-semibold text-slate-900 mb-2">35-50 min</p>
                  <p className="text-[13px] text-slate-500">Service every 15-30 min during peak. Monthly pass ~$380. Free parking at both stations (arrive early).</p>
                </div>
                <div className="p-5">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Highway Access</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {['QEW (east-west)', '403 (northwest)', '407 (toll, fast)'].map(h => (
                      <span key={h} className="font-mono text-xs bg-slate-50 border border-slate-200 text-slate-700 px-2.5 py-1 rounded-full">{h}</span>
                    ))}
                  </div>
                  <p className="text-[13px] text-slate-500">Morning traffic toward Toronto can be heavy. For hybrid workers (2-3 days in office), Burlington often becomes the sweet spot.</p>
                </div>
              </div>
              <p className="text-base text-slate-500 leading-relaxed">
                Many buyers find the commute tradeoff worthwhile for the lifestyle and home size gains. At 2-3 days per week, commute costs drop significantly, and the quality-of-life upgrade compounds daily. Burlington's commute is comparable to many inner-suburb TTC commutes — but with a waterfront at the other end.
              </p>
              <div className="mt-4">
                <Link to="/relocation" className="text-sm text-emerald-600 hover:text-emerald-700 transition-colors flex items-center gap-1">
                  Read the full Relocation Guide <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </section>

            {/* Parks */}
            <section id="parks" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Parks, waterfront & <span className="italic">recreation</span>
              </h2>
              <p className="text-base text-slate-500 leading-relaxed mb-6">
                Outdoor access is one of Burlington's defining features. The city sits between Lake Ontario to the south and the Niagara Escarpment to the north, creating an outdoor lifestyle few GTA cities can match. There are over 300 parks within city limits.
              </p>

              <div className="relative rounded-2xl overflow-hidden mb-8">
                <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/aq0pbyw8_carol-ann-wodehouse-nWmrfcrLloA-unsplash.jpg" alt="Burlington waterfront trail" className="w-full h-64 object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                  <p className="text-xs text-white/80">Burlington Waterfront Trail — 7+ km along Lake Ontario</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-emerald-600 mb-3">Waterfront & Lake</p>
                  <div className="space-y-2">
                    {['Spencer Smith Park — waterfront crown jewel, pier, splash pad, festivals', 'Burlington Waterfront Trail — 7+ km paved lakeside path', 'LaSalle Park — marina, sports fields, family picnics', 'Paletta Lakefront Park — hidden gem, sunset views', 'Sailing, kayaking, and marina access'].map((item, i) => (
                      <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5"></div><span className="text-[13px] text-slate-600">{item}</span></div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-2xl border border-slate-200/80 p-5">
                  <p className="text-[10px] font-semibold tracking-widest uppercase text-blue-600 mb-3">Escarpment & Nature</p>
                  <div className="space-y-2">
                    {['Royal Botanical Gardens — 2,700 acres, Canada\'s largest', 'Mount Nemo Conservation Area — cliff-edge trails, views', 'Kerncliff Park — urban escarpment access', 'Bruce Trail (Burlington section) — part of 900km trail', 'Bronte Creek Provincial Park — camping, swimming'].map((item, i) => (
                      <div key={i} className="flex items-start gap-2"><div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 mt-1.5"></div><span className="text-[13px] text-slate-600">{item}</span></div>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-base text-slate-500 leading-relaxed">
                Residents enjoy cycling trails, waterfront festivals, sailing and marina access, community events year-round, and some of the best hiking in the GTA within a 15-minute drive. This is not just suburbia — it's lifestyle-driven living.
              </p>
            </section>

            {/* CTA 3 */}
            <div className="relative rounded-2xl overflow-hidden p-10 md:p-14">
              <div className="absolute inset-0">
                <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/nfduamj0_shutterstock_1584051751.jpg" alt="Burlington aerial" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-slate-900/75" />
              </div>
              <div className="relative z-10 max-w-lg">
                <h3 className="text-2xl text-white tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>See which Burlington area <span className="italic">fits you</span></h3>
                <p className="text-sm text-white/40 mb-6">6 questions. Under 3 minutes. Every neighbourhood scored to your priorities.</p>
                <Link to="/blueprint">
                  <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3 h-auto text-sm font-semibold hover:-translate-y-px transition-all shadow-xl">
                    Start My Blueprint <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Demographics */}
            <section id="demographics" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <div className="flex items-center gap-2 mb-6">
                <Users className="w-5 h-5 text-slate-500" />
                <h2 className="text-3xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Demographics & <span className="italic">economy</span></h2>
              </div>
              <p className="text-base text-slate-500 leading-relaxed mb-6">
                Burlington is often described as affluent, family-oriented, highly educated, and safe. Median household income exceeds many GTA suburbs, and the city attracts professionals, families, and retirees in roughly equal measure.
              </p>
              <div className="bento-grid grid-cols-2 md:grid-cols-4 rounded-2xl mb-6">
                {[
                  { label: 'Population', value: '~190,000' },
                  { label: 'Median HH Income', value: 'Above GTA avg' },
                  { label: 'Education', value: 'Highly educated' },
                  { label: 'Safety', value: 'Top-tier' },
                ].map(item => (
                  <div key={item.label} className="p-4 text-center">
                    <p className="font-mono text-lg font-semibold text-slate-900">{item.value}</p>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-1">{item.label}</p>
                  </div>
                ))}
              </div>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                Major employment sectors include advanced manufacturing, technology, healthcare, and professional services. Many residents commute to Toronto, Oakville, or Hamilton — though remote work has shifted this significantly since 2020. The city is home to several corporate headquarters and a growing tech presence.
              </p>
              <p className="text-base text-slate-500 leading-relaxed">
                Population growth remains steady, though controlled, due to greenbelt protections that limit outward sprawl. This supply constraint is a key factor in Burlington's property value stability — there is structurally limited land for new development.
              </p>
            </section>

            {/* Cost of Living */}
            <section id="cost" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Cost of living in <span className="italic">Burlington</span>
              </h2>
              <p className="text-base text-slate-500 leading-relaxed mb-6">
                While Burlington is not inexpensive, it delivers strong value relative to Toronto or Oakville. The key advantage is space per dollar — the same budget that buys a Toronto condo or semi gets a Burlington detached home with a yard, garage, and driveway.
              </p>
              <div className="space-y-3 mb-6">
                {[
                  { category: 'Housing', detail: 'More space per dollar than Toronto. Lower price per square foot than Oakville. Condos from $425K, detached from $800K.' },
                  { category: 'Property Taxes', detail: 'Competitive within Halton Region. Halton\'s tax rate is moderate relative to Hamilton or Peel.' },
                  { category: 'Childcare', detail: 'Comparable to GTA averages. Multiple daycare and preschool options throughout the city.' },
                  { category: 'Groceries & Utilities', detail: 'Similar to broader Ontario. Multiple grocery chains (Fortinos, Metro, Farm Boy) compete on pricing.' },
                  { category: 'Transportation', detail: 'GO Transit monthly pass ~$380. Car-dependent for most areas outside downtown. Gas prices track Ontario averages.' },
                ].map((item) => (
                  <div key={item.category} className="bg-white rounded-2xl border border-slate-200/80 p-4">
                    <p className="text-sm font-medium text-slate-900 mb-1">{item.category}</p>
                    <p className="text-[13px] text-slate-500">{item.detail}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <Link to="/budget" className="text-sm text-emerald-600 hover:text-emerald-700 transition-colors flex items-center gap-1">
                  Read the full Budget Planner <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </section>

            {/* Real Estate */}
            <section id="realestate" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp className="w-5 h-5 text-slate-500" />
                <h2 className="text-3xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Burlington real estate <span className="italic">overview</span></h2>
              </div>
              <p className="text-base text-slate-500 leading-relaxed mb-6">
                Burlington's housing market includes detached homes, townhomes, condo apartments, and luxury waterfront estates. Detached homes dominate established neighbourhoods, while condo growth is concentrated downtown and in Burlington East.
              </p>
              <div className="space-y-2 mb-6">
                {[
                  { type: 'Condos', range: '$425K – $900K', where: 'Downtown, Burlington East, Tyandaga' },
                  { type: 'Townhomes', range: '$600K – $1.3M', where: 'All areas except Shoreacres & Kilbride' },
                  { type: 'Detached', range: '$800K – $2.5M+', where: 'All 21 neighbourhoods' },
                  { type: 'Waterfront/Luxury', range: '$1.5M – $4.5M+', where: 'Shoreacres, Roseland' },
                ].map(item => (
                  <div key={item.type} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <div>
                      <span className="text-sm font-medium text-slate-900">{item.type}</span>
                      <span className="text-[11px] text-slate-400 ml-2">{item.where}</span>
                    </div>
                    <span className="font-mono text-sm font-semibold text-slate-900">{item.range}</span>
                  </div>
                ))}
              </div>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                Market cycles typically follow GTA trends but with less volatility — Burlington's structural supply constraints (greenbelt, limited land, controlled growth) provide a floor that many GTA markets lack. Buyers are drawn by stability, school access, lifestyle amenities, and long-term property value preservation.
              </p>
            </section>

            {/* Why Burlington */}
            <section id="why" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Why buyers are moving to <span className="italic">Burlington</span>
              </h2>
              <p className="text-base text-slate-500 leading-relaxed mb-6">
                The most common relocation paths into Burlington are from Toronto (more space, better schools), Mississauga (Halton school board switch), and Hamilton (safety, schools, lifestyle upgrade). Here's what they find:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {[
                  'Waterfront access and lifestyle', 'Top-ranked Halton school board', 'Strong municipal planning', 'Greenbelt-protected land supply',
                  'Lower density than Toronto', 'Balanced urban-suburban feel', 'Safe, low-crime communities', 'Manageable GO Transit commute',
                  'Growing downtown revitalization', 'Strong long-term property values',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5 p-3 bg-emerald-50/50 rounded-xl border border-emerald-100/50">
                    <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-[13px] text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <h3 className="text-xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>Is Burlington a good place to live?</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {[
                  { who: 'For Families', answer: 'Yes. Schools, parks, safety, and community events are strong advantages.' },
                  { who: 'For Retirees', answer: 'Waterfront condos and bungalow communities offer appealing downsizing options.' },
                  { who: 'For Professionals', answer: 'GO Transit access makes commuting manageable. WFH buyers gain the most.' },
                  { who: 'For Investors', answer: 'Limited land supply and stable demand provide long-term confidence.' },
                ].map(item => (
                  <div key={item.who} className="bg-white rounded-2xl border border-slate-200/80 p-5">
                    <p className="text-sm font-medium text-slate-900 mb-1">{item.who}</p>
                    <p className="text-[13px] text-slate-500">{item.answer}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="scroll-mt-32 border-t border-slate-100 pt-12">
              <div className="flex items-center gap-2 mb-6">
                <HelpCircle className="w-5 h-5 text-slate-500" />
                <h2 className="text-3xl tracking-tight" style={{ fontFamily: "'Instrument Serif', serif" }}>Frequently asked <span className="italic">questions</span></h2>
              </div>
              <div className="space-y-4">
                {[
                  { q: 'Is Burlington expensive?', a: 'More affordable than Toronto and Oakville, but higher than many Ontario cities. Condos start from $425K, detached from $800K. You typically get significantly more space per dollar than Toronto.' },
                  { q: 'Is Burlington safe?', a: 'Yes. Burlington and the broader Halton Region consistently rank among Ontario\'s safest municipalities. Crime rates are well below the GTA average.' },
                  { q: 'How far is Burlington from Toronto?', a: 'Approximately 50 km. 45-60 minutes by car depending on traffic, or 35-50 minutes by GO Train from Burlington GO to Union Station.' },
                  { q: 'What are the best neighbourhoods in Burlington?', a: 'It depends on your priorities. Roseland and Shoreacres for luxury, Alton Village and Orchard for families, Burlington East for value, Downtown for walkability. The Buyer Blueprint matches you based on your specific needs.' },
                  { q: 'Is Burlington good for families?', a: 'Very much so. The Halton District School Board is one of Ontario\'s best, there are 300+ parks, extensive recreation programs, low crime, and a family-oriented community culture.' },
                  { q: 'Is the commute from Burlington to Toronto manageable?', a: 'For hybrid workers (2-3 days per week), yes. GO Train runs every 15-30 minutes during peak and takes 35-50 minutes. For 5-day-a-week commuters, it\'s doable but less enjoyable.' },
                  { q: 'What is the population of Burlington?', a: 'Approximately 190,000 as of the latest census. Growth is controlled due to greenbelt protections, which limits sprawl and supports property values.' },
                  { q: 'Does Burlington have good restaurants?', a: 'Yes — especially downtown. Brant Street alone has 50+ restaurants, from upscale Italian to craft cocktail bars. The food scene is independent-driven, not chain-dominated.' },
                  { q: 'What is Burlington known for?', a: 'Waterfront living, the Royal Botanical Gardens, Spencer Smith Park, top-ranked schools, the Sound of Music Festival, and being one of Canada\'s most livable cities.' },
                  { q: 'Is Burlington better than Oakville?', a: 'Different, not objectively better. Burlington is more affordable, has a stronger waterfront identity, and shares the same school board (Halton). Oakville has more established luxury areas and slightly shorter GO commute. Most families who compare both end up choosing based on budget.' },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-5">
                    <p className="text-[14px] font-medium text-slate-900 mb-2">{item.q}</p>
                    <p className="text-[13px] text-slate-500 leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Future */}
            <section className="border-t border-slate-100 pt-12">
              <h2 className="text-3xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
                The future of <span className="italic">Burlington</span>
              </h2>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                Growth is focused around downtown intensification, transit corridors, sustainable development, and lakefront enhancement. Burlington's Official Plan emphasizes managed growth — adding housing density near transit stations while protecting established neighbourhoods and greenspace.
              </p>
              <p className="text-base text-slate-500 leading-relaxed mb-4">
                Greenbelt protections limit over-expansion, which supports long-term property value. New condo development downtown, ongoing Brant Street revitalization, and potential GO service improvements will continue to enhance Burlington's appeal.
              </p>
              <p className="text-base text-slate-700 font-medium">
                Burlington is evolving — but carefully. That balance of growth and preservation is precisely what makes it one of Ontario's most stable and desirable real estate markets.
              </p>
            </section>

            {/* Final CTA */}
            <div className="relative rounded-2xl overflow-hidden p-10 md:p-14 text-center">
              <div className="absolute inset-0">
                <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/swd0mu3s_shutterstock_2675683289.jpg" alt="Burlington" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-slate-900/80" />
              </div>
              <div className="relative z-10 max-w-lg mx-auto">
                <h2 className="text-3xl text-white tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>
                  Burlington deserves serious <span className="italic">consideration</span>
                </h2>
                <p className="text-sm text-white/40 mb-6">
                  It's not just another suburb. It's one of the most balanced cities in Ontario — offering lakefront beauty, community infrastructure, commuter convenience, and real estate stability.
                </p>
                <Link to="/blueprint">
                  <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-8 py-3 h-auto text-sm font-semibold hover:-translate-y-px transition-all shadow-xl">
                    Find My Burlington Neighbourhood <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-36 space-y-6">
              <div className="bg-slate-950 noise-overlay relative rounded-2xl overflow-hidden p-6">
                <div className="relative z-10">
                  <h3 className="text-lg text-white tracking-tight mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>
                    Find your <span className="italic">neighbourhood</span>
                  </h3>
                  <p className="text-[13px] text-white/40 mb-6">
                    6 questions. Under 3 minutes. All 21 Burlington neighbourhoods scored to your priorities.
                  </p>
                  <Link to="/blueprint">
                    <Button className="w-full bg-white text-slate-900 hover:bg-slate-100 rounded-full py-3 font-medium h-auto text-sm hover:-translate-y-px transition-all">
                      Start Blueprint <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/80 p-5">
                <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Quick facts</p>
                <div className="space-y-2.5 text-[13px]">
                  {[
                    ['Population', '~190,000'],
                    ['Region', 'Halton'],
                    ['Province', 'Ontario, Canada'],
                    ['GO to Toronto', '35-50 min'],
                    ['School Board', 'Halton DSB (top-ranked)'],
                    ['Neighbourhoods', '21 distinct areas'],
                    ['Homes from', '$425K'],
                    ['Waterfront', 'Lake Ontario'],
                  ].map(([label, value]) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-slate-400">{label}</span>
                      <span className="text-slate-700 font-medium text-right">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/80 p-5">
                <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Explore more</p>
                <div className="space-y-2">
                  {[
                    { to: '/neighbourhoods', label: 'All 12 Neighbourhoods' },
                    { to: '/schools', label: 'Schools Guide' },
                    { to: '/relocation', label: 'Relocation Guide' },
                    { to: '/budget', label: 'Budget Planner' },
                    { to: '/guides', label: 'All 66 Guides' },
                  ].map(link => (
                    <Link key={link.to} to={link.to} className="block text-sm text-slate-500 hover:text-slate-900 transition-colors flex items-center gap-1.5">
                      <ArrowRight className="w-3 h-3" /> {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
